import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Monitor, Smartphone, Laptop, ShieldCheck, LogOut, AlertTriangle, Globe,
} from "lucide-react";
import { toast } from "sonner";

function deviceIcon(os) {
  if (os === "Android" || os === "iOS") return Smartphone;
  if (os === "macOS") return Laptop;
  return Monitor;
}

function formatRelative(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `il y a ${hr} h`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `il y a ${d} j`;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

export default function DeviceSessions() {
  const [sessions, setSessions] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data.sessions);
    } catch {
      setSessions([]);
    }
  };

  useEffect(() => { load(); }, []);

  const onRevoke = async (sid, isCurrent) => {
    setRevoking(sid);
    try {
      await api.post(`/auth/sessions/${sid}/revoke`);
      if (isCurrent) {
        toast.success("Vous avez été déconnecté de cet appareil.");
        localStorage.removeItem("afrimeet_token");
        localStorage.removeItem("afrimeet_user");
        window.location.href = "/auth/login";
      } else {
        toast.success("Session révoquée");
        load();
      }
    } catch {
      toast.error("Impossible de révoquer cette session");
    } finally {
      setRevoking(null);
    }
  };

  if (sessions === null) return null;

  const current = sessions.find((s) => s.is_current);
  const past = sessions.filter((s) => !s.is_current);

  return (
    <Card className="p-8 bg-af-card border-af rounded-md" data-testid="device-sessions-card">
      <div className="flex items-start gap-3 mb-2">
        <div className="h-10 w-10 rounded-md bg-[#F5EFD8] border border-af flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-terracotta" />
        </div>
        <div>
          <h2 className="font-display font-bold text-xl">Appareils connectés</h2>
          <p className="text-sm text-af-secondary">Historique de vos 20 dernières connexions. Une seule session active à la fois.</p>
        </div>
      </div>

      {current && (
        <div className="mt-6 mb-8">
          <SessionRow
            session={current}
            highlight
            revokeLabel="Me déconnecter de cet appareil"
            revoking={revoking === current.id}
            onRevoke={() => onRevoke(current.id, true)}
            testIdPrefix="current-session"
          />
        </div>
      )}

      {past.length > 0 && (
        <div className="border-t border-af pt-6">
          <div className="text-xs uppercase tracking-widest text-af-muted font-semibold mb-3">
            Connexions précédentes
          </div>
          <ul className="space-y-2">
            {past.map((s, i) => (
              <SessionRow key={s.id} session={s} testIdPrefix={`past-session-${i}`} />
            ))}
          </ul>
          {past.some((s) => !s.revoked_at) && (
            <div className="mt-4 flex items-start gap-2 text-xs text-[#9F1239] bg-[#FFF1F2] border border-[#FECDD3] rounded-md p-3">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Une session apparaît comme active sur cette ligne. Si vous ne la reconnaissez pas, changez immédiatement votre mot de passe.</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function SessionRow({ session, highlight, revokeLabel, revoking, onRevoke, testIdPrefix }) {
  const Icon = deviceIcon(session.os);
  const where = [session.city, session.country].filter(Boolean).join(", ") || "Localisation inconnue";
  const isRevoked = !!session.revoked_at;
  return (
    <div
      data-testid={testIdPrefix}
      className={
        "flex items-center gap-4 p-4 rounded-md border " +
        (highlight ? "bg-[#F5EFD8] border-[#E5D9B8]" : "bg-white border-af")
      }
    >
      <div className={"h-11 w-11 rounded-md flex items-center justify-center shrink-0 " + (highlight ? "bg-white border border-af" : "bg-af-subtle")}>
        <Icon className={"h-5 w-5 " + (highlight ? "text-terracotta" : "text-af-secondary")} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm text-af">
            {session.browser} • {session.os}
          </div>
          {highlight && (
            <Badge className="bg-terracotta text-white border-0 rounded-sm text-[10px] uppercase tracking-wider" data-testid="current-session-badge">
              Session actuelle
            </Badge>
          )}
          {isRevoked && (
            <Badge variant="outline" className="border-af text-af-muted text-[10px] uppercase tracking-wider">
              Terminée
            </Badge>
          )}
        </div>
        <div className="text-xs text-af-secondary mt-0.5 flex items-center gap-1.5 flex-wrap">
          <Globe className="h-3 w-3" />
          <span>{where}</span>
          <span className="text-af-muted">•</span>
          <span className="font-mono text-[11px]">{session.ip}</span>
          <span className="text-af-muted">•</span>
          <span>{formatRelative(session.created_at)}</span>
        </div>
      </div>
      {highlight && onRevoke && (
        <Button
          onClick={onRevoke}
          disabled={revoking}
          variant="outline"
          size="sm"
          data-testid="revoke-current-btn"
          className="border-[#9F1239] text-[#9F1239] hover:bg-[#FFF1F2] hover:text-[#9F1239] rounded-md whitespace-nowrap"
        >
          <LogOut className="h-3.5 w-3.5 mr-1.5" />
          {revoking ? "…" : revokeLabel}
        </Button>
      )}
    </div>
  );
}
